import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaExternalLinkAlt, FaGithub} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  image?: string;
  video?: string;
  status?: string;
  tags: string[];
  liveUrl?: string;
  githubUrl?: string;
  internalPath?: string;
  visual?: 'driveskill' | 'webar';
}

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);

  const projects: Project[] = [
    {
      id: '16',
      title: 'Pixify Archi Walkthrough',
      category: 'realtime',
      description: 'Unity architectural walkthrough demo built by RedRender for Pixify with first-person navigation, baked lighting, and smooth day/night transition.',
      image: '/pixify-logo.png',
      video: '/pixify-archi-walkthrough-demo.mp4',
      status: 'Made for Pixify',
      tags: ['Unity', 'First Person', 'Baked Lighting', 'Day/Night'],
      internalPath: '/pixify-archi-walkthrough'
    },
    {
      id: '14',
      title: 'InoviqSpace',
      category: 'realtime',
      description: 'Native interior visualization and virtual showroom platform with Unity HDRP, VR, real-time collaboration, and built-in communication.',
      video: '/inoviqspace-demo.mp4',
      status: 'Demo Available',
      tags: ['Unity HDRP', 'Ray Tracing', 'VR', 'Real-Time Multiplayer'],
      internalPath: '/inoviqspace'
    },
    {
      id: '17',
      title: 'Web AR Model Viewer',
      category: 'arvr',
      description: 'Browser-based AR viewer with default chair model, GLB/GLTF upload, real-time preview, and mobile AR launch support.',
      image: '/ar-viewer-thumbnail.jpg',
      tags: ['Web AR', 'GLB Upload', '3D Viewer', 'Mobile AR'],
      internalPath: '/web-ar'
    },
    {
      id: '11',
      title: 'DriveSkill VR',
      category: 'arvr',
      description: 'Immersive car driving simulator in virtual reality for realistic practice and training.',
      image: '/driveskill-vr-thumbnail.jpg',
      tags: ['Unity', 'VR', 'Driving'],
      internalPath: '/driveskill-vr'
    },
    {
      id: '12',
      title: 'VR Walkthrough',
      category: 'arvr',
      description: 'Immersive virtual reality walkthrough for realistic space presentation and exploration.',
      image: '/vr-walkthrough-thumbnail.jpg',
      tags: ['Unity', 'VR', 'Walkthrough'],
      internalPath: '/vr-walkthrough'
    },
    {
      id: '18',
      title: 'Dead Inside',
      category: 'games',
      description: 'A Granny-inspired mobile horror game with intense jump scares, hide-and-seek stealth, and room escape puzzles.',
      image: '/dead-inside-banner.png',
      status: 'Work in Progress',
      tags: ['Unity', 'Android', 'Jump Scares', 'Horror'],
      internalPath: '/dead-inside'
    },
    {
      id: '15',
      title: 'Office Outlaws',
      category: 'games',
      description: 'A stealth office hide-and-seek mobile game where players outsmart the boss and survive each level.',
      image: '/office-outlaws-hero.png',
      status: 'Coming Soon on Play Store',
      tags: ['Unity', 'Android', 'Stealth'],
      internalPath: '/office-outlaws'
    },
    {
      id: '10',
      title: 'JumpFroggo',
      category: 'games',
      description: 'A fun frog jump game where players leap across floating leaves and chase high scores.',
      image: '/jumpfroggo-banner.png',
      tags: ['Unity', 'C#', 'Android'],
      internalPath: '/jumpfroggo'
    },
    {
      id: '13',
      title: 'Bathroom Configurator',
      category: 'realtime',
      description: 'Interactive bathroom configurator for real-time design customization and premium visualization.',
      image: 'https://img.youtube.com/vi/uGDhoFjksEo/maxresdefault.jpg',
      tags: ['Unreal Engine', 'Configurator', 'Interior'],
      internalPath: '/bathroom-configurator'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'games', name: 'Games' },
    { id: 'arvr', name: 'AR/VR' },
    { id: 'realtime', name: 'Real-Time Applications' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  const projectsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'RedRender Featured Projects',
    url: `${siteUrl}/portfolio`,
    itemListElement: projects.map((project, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'CreativeWork',
        name: project.title,
        description: project.description,
        url: project.internalPath ? `${siteUrl}${project.internalPath}` : `${siteUrl}/portfolio`,
        image: project.image?.startsWith('http') ? project.image : `${siteUrl}${project.image || '/logo.png'}`
      }
    }))
  };

  const renderProjectVisual = (project: Project) => {
    if (project.visual === 'driveskill') {
      return (
        <div className="relative h-64 overflow-hidden bg-[radial-gradient(circle_at_50%_28%,rgba(255,85,85,0.24),transparent_24%),linear-gradient(160deg,rgba(18,18,21,0.98),rgba(3,3,5,1))]">
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70 group-hover:opacity-85 transition-opacity duration-300"></div>
          <div className="absolute left-1/2 top-8 h-16 w-36 -translate-x-1/2 rounded-full border border-red-500/35 bg-red-500/10 shadow-[0_0_45px_rgba(239,68,68,0.22)]" />
          <div className="absolute left-1/2 top-20 h-28 w-28 -translate-x-1/2 rounded-full border-[12px] border-red-500/75 bg-black/30 shadow-[0_0_55px_rgba(239,68,68,0.35)]">
            <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-white/10" />
            <div className="absolute left-1/2 top-1/2 h-2 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-200/70" />
            <div className="absolute left-1/2 top-1/2 h-20 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-200/70" />
          </div>
          <div className="absolute inset-x-[18%] bottom-8 h-20 overflow-hidden rounded-t-3xl border-x border-t border-red-500/30 bg-red-500/10">
            <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-red-300/45" />
            <div className="absolute bottom-0 left-1/2 h-16 w-[150%] -translate-x-1/2 rounded-t-full border-t border-white/15 bg-white/5" />
          </div>

          <div
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
              hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span className="p-4 bg-red-500 rounded-full text-white transition-all duration-300 transform group-hover:scale-110">
              <FaExternalLinkAlt className="text-xl" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="relative h-64 overflow-hidden">
        {project.id === '16' ? (
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_18%_12%,rgba(236,72,153,0.18),transparent_22rem),radial-gradient(circle_at_80%_78%,rgba(59,130,246,0.16),transparent_22rem),linear-gradient(135deg,rgba(12,12,16,1),rgba(3,3,5,1))]">
            <video
              src={project.video}
              className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-110"
              muted
              playsInline
              preload="metadata"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/25" />
            <div className="relative mx-6 rounded-2xl border border-white/10 bg-black/45 p-5 shadow-2xl shadow-black/40 backdrop-blur-md">
              <img src={project.image} alt="Pixify Design Agency logo" className="max-h-20 w-auto object-contain" />
            </div>
          </div>
        ) : (
          <img
            src={project.video ? '/inoviqspace-poster.jpg' : project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
        {project.status && (
          <div
            className={`absolute left-5 top-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] backdrop-blur-md ${
              project.status.toLowerCase().includes('progress')
                ? 'border border-red-500/50 bg-red-950/60 text-red-300 shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                : 'border border-cyan-300/40 bg-cyan-400/15 text-cyan-100'
            }`}
          >
            {project.status.toLowerCase().includes('progress') && (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
            )}
            {project.status}
          </div>
        )}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span className="p-4 bg-red-500 rounded-full text-white transition-all duration-300 transform group-hover:scale-110">
            <FaExternalLinkAlt className="text-xl" />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-24 pb-20">
      <SEO
        title="Portfolio - Games, AR/VR, 3D Visualization & Interactive Apps | RedRender"
        description="Explore RedRender projects including Dead Inside, Office Outlaws, JumpFroggo, InoviqSpace, DriveSkill VR, VR Walkthrough, and real-time visualization configurators."
        path="/portfolio"
        image="/inoviqspace-poster.jpg"
        keywords={[
          'RedRender portfolio',
          'Dead Inside mobile horror',
          'Unity projects',
          'AR VR portfolio',
          'game development portfolio',
          '3D visualization projects',
          'interactive application portfolio'
        ]}
        jsonLd={projectsJsonLd}
      />
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:100px_100px] pointer-events-none"></div>
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Page Header */}
        <div className="text-center mb-16 animate-slide-up">
          <div className="inline-flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-full px-5 py-2 backdrop-blur-sm mb-6">
            <span className="text-red-500 font-semibold text-sm font-display">Our Work</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-display">
            Featured <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">Projects</span>
          </h1>
          <p className="text-gray-400 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of successful projects that showcase our expertise and creativity
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 animate-slide-up">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeFilter === cat.id
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/50'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 hover:border-red-500/50 transition-all duration-500 animate-scale-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {project.internalPath ? (
                <Link to={project.internalPath} className="block h-full">
                  {/* Image Container */}
                  {renderProjectVisual(project)}

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-3 font-display group-hover:text-red-400 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-400 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Link>
              ) : (
                <>
                  {/* Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                    
                    {/* Overlay Icons */}
                    <div className={`absolute inset-0 flex items-center justify-center gap-4 transition-opacity duration-300 ${
                      hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
                    }`}>
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          className="p-4 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors transform hover:scale-110 duration-300"
                          onClick={(e) => e.preventDefault()}
                        >
                          <FaExternalLinkAlt className="text-xl" />
                        </a>
                      )}
                      {project.githubUrl && (
                        <a
                          href={project.githubUrl}
                          className="p-4 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors transform hover:scale-110 duration-300"
                          onClick={(e) => e.preventDefault()}
                        >
                          <FaGithub className="text-xl" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-3 font-display group-hover:text-red-400 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {project.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-400 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Corner Accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-20 animate-slide-up">
          {[
            { number: '100+', label: 'Projects Completed' },
            { number: '50+', label: 'Happy Clients' },
            { number: '98%', label: 'Client Satisfaction' }
          ].map((stat, index) => (
            <div
              key={index}
              className="text-center bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-red-500/50 transition-all duration-300 group"
            >
              <h3 className="text-4xl font-bold text-red-500 mb-2 font-display group-hover:scale-110 transition-transform">
                {stat.number}
              </h3>
              <p className="text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
